import { Component, OnInit, Inject } from "@angular/core";
import { CeRequestOptions } from "../../request-header/request-header.component";
import {
  RequestDesign,
  RequestService,
  SavedRequest,
  RecurrencePattern,
  TissueCollectionType,
  RequestedSampling
} from "../../../shared/services/request.service";
import { ActivatedRoute, Router } from "@angular/router";
import {
  ExperimentDesignFormModel,
  ExperimentDetailsFormModel
} from "../../services/request-form.service";
import { Observable } from "rxjs";
import { RequestFormModelService } from "../../services/request-form-model.service";
import { ExperimentDesignFormBuilderService } from "../experiment-design-form/experiment-design-form-builder.service";
import { FormGroup, Validators, FormBuilder, ValidatorFn, FormArray, FormControl } from "@angular/forms";
import { ExperimentDesignFormValidatorsService } from "../experiment-design-form/experiment-design-form-validators.service";
import { AlertService } from "../../../shared/services/alert.service";
import { HttpErrorResponse } from "@angular/common/http";
import { LoadingWrapperService } from "../../../shared/services/loading-wrapper.service";
import { finalize, switchMap, catchError } from "rxjs/operators";
import { asEnumerable } from "linq-es2015";
import { GeNameValidator } from "../pollination-instructions/validators/ge-name.validator";
import { CE_REQUEST_OPTIONS, ResolvedRequestOptionsService } from "../../services/resolved-request-options";
import { ExperimentDesignShowHideService } from "../../services/experiment-design-show-hide.service";
import { SamplingDateCalculationStrategy } from "../sampling-needs/sampling-needs-form/sampling-needs-form.component";

@Component({
  selector: "app-experiment-design-form-page",
  templateUrl: "./experiment-design-form-page.component.html",
  providers: [
    ExperimentDesignFormBuilderService,
    ExperimentDesignFormValidatorsService,
    RequestFormModelService,
    { provide: CE_REQUEST_OPTIONS, useClass: ResolvedRequestOptionsService }
  ]
})
export class ExperimentDesignFormPageComponent implements OnInit {
  private id: number;
  public options: CeRequestOptions;
  public requestDetails: ExperimentDetailsFormModel;
  public requestDesign: RequestDesign;
  public experimentDesignFormGroup: FormGroup;

  public saving: boolean = false;
  public submitting: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private formService: ExperimentDesignFormBuilderService,
    private router: Router,
    private requestService: RequestService,
    private loadingService: LoadingWrapperService,
    private formModelService: RequestFormModelService,
    private alertService: AlertService,
    private formBuilder: FormBuilder,
    private geNameValidator: GeNameValidator,
    private formValidators: ExperimentDesignFormValidatorsService,
    private experimentDesignShowHideService: ExperimentDesignShowHideService,
    @Inject(CE_REQUEST_OPTIONS) private routeData: ResolvedRequestOptionsService
  ) { }

  ngOnInit() {
    this.id = this.route.snapshot.params["id"];
    this.options = this.routeData.requestOptions;
    this.requestDesign = this.route.snapshot.data["requestDesign"];

    const details = this.route.snapshot.data["requestDetails"];
    this.requestDetails = this.formModelService.toDetailsFormModel(details);

    this.experimentDesignFormGroup = this.formService.build({
      design: this.requestDesign,
      cropId: this.requestDetails.crop,
      plantingDate: this.requestDetails.plantingDate,
      requestPurposeId: this.requestDetails.requestPurpose,
      pesticideInstructions: this.options.pesticideInstructions
    });

    this.setupForm();
  }

  public save(): void {
    this.saving = true;
    this.loadingService.setMessage("Saving...");

    this.saveDesign()
      .pipe(
        switchMap(m => {
          return this.requestService.getDesign(m.id);
        }),
        catchError(e => {
          this.handleError(e, "Error getting design. Please contact support.");
          return Observable.throw(e);
        }),
        finalize(() => {
          this.loadingService.clearMessage();
          this.saving = false;
        })
      )
      .subscribe(
        r => {
          this.requestDesign = r;
          this.alertService.success("Experiment Design saved!");
        },
        e => {
          this.handleError(e, "Error saving request. Please contact support.");
        }
      );
  }

  public saveAndSubmit(): void {
    this.submitting = true;
    this.loadingService.setMessage("Saving...");

    this.saveDesign()
      .catch(e => {
        this.handleError(e, "Error saving design. Please contact support.");
        return Observable.throw(e);
      })
      .pipe(
        switchMap(m => {
          return this.requestService.submitRequest(m.id);
        })
      )
      .subscribe(
        r => {
          this.router.navigate([`/requests/view/${this.id}`]);
        },
        e => {
          this.submitting = false;
          this.loadingService.clearMessage();
          this.handleError(e, "Error submitting request. Please contact support.");
          if (e instanceof HttpErrorResponse && e.status === 400) {
            console.log("Backend validation errors...");
            console.log(e.error);
            this.alertService.error(e.error);
          }
        }
      );
  }

  public goToDetails(): void {
    this.router.navigate([`/requests/edit/${this.id}/details`]);
  }

  private saveDesign(): Observable<SavedRequest> {
    const model = this.experimentDesignFormGroup.value as ExperimentDesignFormModel;
    const apiModel = this.formModelService.toDesignApiModel(model);
    apiModel.id = this.id;
    return this.requestService.saveDesign(apiModel);
  }

  private setupForm(): void {
    let formModel: ExperimentDesignFormModel = null;

    if (!this.requestDesign) {
      formModel = {
        numberOfReps: 1,
        numberOfTreatments: 1,
        numberOfSeedsPerCell: 1,
        sprayStudy: false,
        recurrencePattern: RecurrencePattern.None,
        recurrenceWeekInterval: 1,
        recurrenceDays: [],
        recurrenceEndDate: null,
        seedNeeds: [],
        indexing: false,
        requestedSamplings: [],
        pesticidesAllowed: null,
        pesticidesAllowedAfterDays: null,
        pesticideLifeOfPlant: null,
        harvestInstructions: {
          embryoRescue: false,
          minimumSeedsToReturn: 1,
          forAdvancement: true,
          haploidInduction: false,
          manyToOne: false,
          uniquePedigree: false
        },
        pollinationInstructions: {
          pollinationPreferences: [],
          pollinationCategory: null,
          donorsNeeded: false
        },
        treatments: [],
        seedRequestDesired: true
      };
    } else {
      formModel = this.formModelService.toDesignFormModel(this.requestDesign);
    }

    this.experimentDesignFormGroup.patchValue(formModel, {
      onlySelf: true,
      emitEvent: true
    }); 

    if (this.requestDesign) {
      this.initializePollinationInstructions();
      this.buildSamplingNeeds();
      this.buildSprayBoothStudyFormGroup();
    }

    this.experimentDesignShowHideService.updateView(this.experimentDesignFormGroup, this.requestDetails.requestPurpose);
    this.experimentDesignFormGroup.updateValueAndValidity();
  }

  private initializePollinationInstructions(): void {
    if (this.requestDesign.pollinationInstructions === null) return;

    const pollinationTypes = asEnumerable(this.options.pollinationTypes);
    const pollinationInstructions = <FormGroup>this.experimentDesignFormGroup.get("pollinationInstructions");
    const pollinationPreferences = this.formBuilder.array([]);
    asEnumerable(this.requestDesign.pollinationInstructions.preferences)
      .OrderBy(g => g.orderNumber)
      .ToArray()
      .forEach(g => {

        const items = this.formBuilder.array([]);
        asEnumerable(g.items)
          .OrderBy(i => i.orderNumber)
          .ToArray()
          .forEach(i => {
            const geName = this.formBuilder.control({ value: null, disabled: false }, []);

            if (pollinationTypes.Single(t => t.id === i.pollinationTypeId).geNameRequired) {
              geName.setValidators([
                Validators.required,
                Validators.minLength(3)
              ]);
              geName.setAsyncValidators(
                this.geNameValidator.validateGeName()
              );
            }

            geName.patchValue(i.geName); // patch it after setting up validators to avoid ExpressionChangedAfterItHasBeenCheckedError
            geName.updateValueAndValidity();

            items.push(this.formBuilder.group({
              conjunction: this.formBuilder.control({ value: i.conjunction, disabled: false }),
              pollinationType: this.formBuilder.control({ value: i.pollinationTypeId, disabled: false }, []),
              geName: geName
            }))
          });

        pollinationPreferences.push(this.formBuilder.group({
          conjunction: this.formBuilder.control({ value: g.conjunction, disabled: false }),
          items: items
        }, { validator: this.formValidators.pollinationPreferenceGroupValidator() }));
      });

    pollinationInstructions.setControl("pollinationPreferences", pollinationPreferences);
  }

  private buildSamplingNeeds(): void {
    if (!this.requestDesign || !this.requestDesign.requestedSamplings) {
      return;
    }

    const samplingNeeds = this.formBuilder.array([]);

    asEnumerable(this.requestDesign.requestedSamplings)
      .OrderBy(l => l.order)
      .ToArray()
      .forEach(e => {
        let newGroup: FormGroup = null;

        if (e.tissueCollectionType === TissueCollectionType.Standard) {
          newGroup = this.getStandardSamplingNeedsFormGroup(e);
        }
        else {
          newGroup = this.getCustomSamplingNeedsFormGroup(e);
        }

        samplingNeeds.push(newGroup);
      });

    this.experimentDesignFormGroup.setControl('requestedSamplings', samplingNeeds);
  }

  private getStandardSamplingNeedsFormGroup(r: RequestedSampling): FormGroup {
    return this.formBuilder.group({
      laboratory: this.formBuilder.control(r.laboratoryId, Validators.required),
      tissueType: this.formBuilder.control(r.tissueTypeId, Validators.required),
      assay: this.formBuilder.control(r.laboratoryAssayId, Validators.required),
      tissueCollectionType: this.formBuilder.control(TissueCollectionType.Standard, Validators.required),
      samplingDateCalculationStrategy: this.formBuilder.control(SamplingDateCalculationStrategy.GrowthStage),
      growthStage: this.formBuilder.control({ value: null, disabled: true }),
      daysAfterPlanting: this.formBuilder.control({ value: null, disabled: true }),
      order: r.order
    });
  }

  private getCustomSamplingNeedsFormGroup(r: RequestedSampling): FormGroup {
    if (r.growthStageId !== null) {
      return this.getGrowthStageSamplingNeedsFormGroup(r);
    }
    return this.getDaysAfterPlantingSamplingNeedsFormGroup(r);
  }

  private getGrowthStageSamplingNeedsFormGroup(r: RequestedSampling): FormGroup {
    return this.formBuilder.group({
      laboratory: this.formBuilder.control(r.laboratoryId, Validators.required),
      tissueType: this.formBuilder.control(r.tissueTypeId, Validators.required),
      assay: this.formBuilder.control(r.laboratoryAssayId, Validators.required),
      tissueCollectionType: this.formBuilder.control(TissueCollectionType.Custom, Validators.required),
      samplingDateCalculationStrategy: this.formBuilder.control(SamplingDateCalculationStrategy.GrowthStage),
      growthStage: this.formBuilder.control(r.growthStageId, Validators.required),
      daysAfterPlanting: this.formBuilder.control({ value: null, disabled: true }),
      order: r.order
    });
  }

  private getDaysAfterPlantingSamplingNeedsFormGroup(r: RequestedSampling): FormGroup {
    return this.formBuilder.group({
      laboratory: this.formBuilder.control(r.laboratoryId, Validators.required),
      tissueType: this.formBuilder.control(r.tissueTypeId, Validators.required),
      assay: this.formBuilder.control(r.laboratoryAssayId, Validators.required),
      tissueCollectionType: this.formBuilder.control(TissueCollectionType.Custom, Validators.required),
      samplingDateCalculationStrategy: this.formBuilder.control(SamplingDateCalculationStrategy.DaysAfterPlanting),
      growthStage: this.formBuilder.control({ value: null, disabled: true }),
      daysAfterPlanting: this.formBuilder.control(r.daysAfterPlanting, [Validators.required, Validators.min(0)]),
      order: r.order
    });
  }

  private buildSprayBoothStudyFormGroup() {
     this.requestDesign.treatments.forEach(currentTreatment => 
      {
        var treatment = this.experimentDesignFormGroup.get("treatments") as FormArray;
        var currentTreatmentFormArray = treatment.controls[currentTreatment.treatmentNumber - 1] as FormGroup;
        var newForm = this.formBuilder.array(
          currentTreatment.sprayBoothStudy.map((data) => {
              return this.formBuilder.group(
                {
                  actives: this.formBuilder.array(
                    (data && data.actives ? data.actives : []).map((a) => {
                      return this.formBuilder.group({
                        activeId: [a.activeId != null ? a.activeId : null, [Validators.required]],
                        rateValue: [a.rateValue != null ? a.rateValue : null, [Validators.required, Validators.pattern(/^(\d{0,10}\.\d{1,10}|\d{1,10})$/)]],
                        rateUnitId: [a.rateUnitId != null ? a.rateUnitId : null, [Validators.required]],
                        relativeFieldRate: [a.relativeFieldRate != null ? a.relativeFieldRate : null, [Validators.required, Validators.pattern(/^(\d{0,10}\.\d{1,10}|\d{1,10})$/)]]
                      });
                    })
                  ),
                  adjuvants: this.formBuilder.array(
                    (data && data.adjuvants ? data.adjuvants : []).map((a) => {
                      return this.formBuilder.group({
                        adjuvantId: [a.adjuvantId != null ? a.adjuvantId : null, [Validators.required]],
                        adjuvantValue: [a.adjuvantValue != null ? a.adjuvantValue : null, [Validators.required, Validators.pattern(/^(\d{0,10}\.\d{1,10}|\d{1,10})$/)]]
                      });
                    })
                  ),
                  applicationTimepointId: [data && data.applicationTimepointId != null ? data.applicationTimepointId : null, [Validators.required]],
                  applicationVolumeId: [data && data.applicationVolumeId != null ? data.applicationVolumeId : null, [Validators.required]]
                },
              { validator: this.formValidators.atLeastOneActiveOrAdjuvantValidator() },
              );
          })
        );
         currentTreatmentFormArray.setControl('sprayBoothStudy', newForm);
      }
    );
  }

  private handleError(e, toastMessage: string) {
    console.error(`Status ${e.status}. ${e.message}`, "Error");
    this.alertService.error(toastMessage);
  }
}



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-4/_work/202/s/src/app/request-creation/experiment-design/experiment-design-form-page/experiment-design-form-page.component.ts